import { ShoppingBag, MessageCircle, Palette, Truck } from "lucide-react";

const stepsRu = [
  { number: "01", icon: ShoppingBag, title: "Выбираете букет", description: "Выберите букет из каталога или напишите нам в WhatsApp", color: "from-pink-500 to-rose-500" },
  { number: "02", icon: MessageCircle, title: "Оформляете заказ", description: "Укажите адрес и удобное время доставки цветов", color: "from-violet-500 to-purple-500" },
  { number: "03", icon: Palette, title: "Собираем букет", description: "Флорист создаёт букет и отправляет вам фото", color: "from-orange-500 to-amber-500" },
  { number: "04", icon: Truck, title: "Доставляем", description: "Курьер доставит свежий букет в указанное время", color: "from-emerald-500 to-teal-500" },
];

const stepsEn = [
  { number: "01", icon: ShoppingBag, title: "Choose a bouquet", description: "Pick from our catalog or message us on WhatsApp", color: "from-pink-500 to-rose-500" },
  { number: "02", icon: MessageCircle, title: "Place your order", description: "Provide the address and preferred delivery time", color: "from-violet-500 to-purple-500" },
  { number: "03", icon: Palette, title: "We arrange it", description: "Our florist creates the bouquet and sends you a photo", color: "from-orange-500 to-amber-500" },
  { number: "04", icon: Truck, title: "We deliver", description: "Our courier delivers the fresh bouquet on time", color: "from-emerald-500 to-teal-500" },
];

const stepsTr = [
  { number: "01", icon: ShoppingBag, title: "Buket secin", description: "Katalogdan secin veya WhatsApp'tan bize yazin", color: "from-pink-500 to-rose-500" },
  { number: "02", icon: MessageCircle, title: "Siparis verin", description: "Adresi ve tercih ettiginiz teslimat zamanini belirtin", color: "from-violet-500 to-purple-500" },
  { number: "03", icon: Palette, title: "Hazirliyoruz", description: "Floristimiz buketi hazirlar ve size fotograf gonderir", color: "from-orange-500 to-amber-500" },
  { number: "04", icon: Truck, title: "Teslim ediyoruz", description: "Kuryemiz taze buketi zamaninda teslim eder", color: "from-emerald-500 to-teal-500" },
];

// Curved arrow for horizontal layout
const HorizontalArrow = ({ flip = false }) => (
  <svg 
    viewBox="0 0 60 40" 
    fill="none" 
    className={`w-12 sm:w-16 h-8 flex-shrink-0 ${flip ? 'scale-y-[-1]' : ''}`}
  >
    <path 
      d="M5 20 Q 30 5, 50 20" 
      stroke="url(#hArrowGrad)" 
      strokeWidth="2" 
      strokeLinecap="round"
      fill="none"
      strokeDasharray="4 4"
    />
    <path 
      d="M45 15 L 52 20 L 45 25" 
      stroke="url(#hArrowGrad)" 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <defs>
      <linearGradient id="hArrowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#F472B6" />
        <stop offset="100%" stopColor="#FB7185" />
      </linearGradient>
    </defs>
  </svg>
);

// Vertical arrow for mobile
const VerticalArrow = () => (
  <svg 
    viewBox="0 0 40 50" 
    fill="none" 
    className="w-8 h-10 mx-auto"
  >
    <path 
      d="M20 5 Q 35 25, 20 40" 
      stroke="url(#vArrowGrad)" 
      strokeWidth="2" 
      strokeLinecap="round"
      fill="none"
      strokeDasharray="4 4"
    />
    <path 
      d="M15 35 L 20 43 L 25 35" 
      stroke="url(#vArrowGrad)" 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <defs>
      <linearGradient id="vArrowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F472B6" />
        <stop offset="100%" stopColor="#FB7185" />
      </linearGradient>
    </defs>
  </svg>
);

export const HowWeWorkSection = ({ lang = "ru" }) => {
  const steps = lang === "tr" ? stepsTr : (lang === "en" ? stepsEn : stepsRu);
  const titleMain = lang === "tr" ? "Nasil" : (lang === "en" ? "How" : "Как");
  const titleAccent = lang === "tr" ? "calisiyoruz" : (lang === "en" ? "we work" : "мы работаем");
  return (
    <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-pink-50 overflow-hidden" data-testid="how-we-work-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {titleMain}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">{titleAccent}</span>
          </h2>
        </div>

        {/* Desktop Layout - Horizontal */}
        <div className="hidden md:flex items-center justify-center gap-2">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const offsets = ['translate-y-0', 'translate-y-4', '-translate-y-2', 'translate-y-3'];
            return (
              <div key={index} className="flex items-center">
                {/* Step Card */}
                <div 
                  className={`bg-white rounded-2xl p-5 shadow-lg shadow-pink-100/50 border border-pink-100 hover:shadow-xl hover:shadow-pink-200/50 transition-all group w-[180px] lg:w-[200px] ${offsets[index]}`}
                  data-testid={`step-${index}`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`w-10 h-10 lg:w-11 lg:h-11 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <span className={`text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.color}`}>
                      {step.number}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">{step.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.description}</p>
                </div>
                
                {/* Arrow between steps */}
                {index < steps.length - 1 && (
                  <div className={`mx-1 ${index % 2 === 0 ? 'mt-6' : '-mt-4'}`}>
                    <HorizontalArrow flip={index % 2 === 1} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile Layout - Vertical with arrows */}
        <div className="md:hidden flex flex-col items-center gap-2">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const offsets = ['translate-x-4', '-translate-x-4', 'translate-x-6', '-translate-x-2'];
            return (
              <div key={index} className="flex flex-col items-center w-full">
                {/* Step Card */}
                <div 
                  className={`bg-white rounded-2xl p-4 shadow-lg shadow-pink-100/50 border border-pink-100 w-[85%] max-w-[280px] ${offsets[index]}`}
                  data-testid={`step-mobile-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.color}`}>
                          {step.number}
                        </span>
                        <h3 className="text-sm font-bold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Vertical Arrow */}
                {index < steps.length - 1 && (
                  <div className="py-1">
                    <VerticalArrow />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowWeWorkSection;
